/**
 * Reordenar imóveis arrastando (Dashboard e lista de Imóveis, out/2026).
 *
 * `bind(id)` devolve as props de arrastar (HTML5 drag-and-drop) para o item e
 * `handleKey(id)` move com as setas do teclado (← ↑ antes, → ↓ depois) — o
 * arrastar nativo não funciona no toque nem no teclado. A ordem é gravada
 * otimista no Redux e depois na API (`PATCH /consumer-units/order`); se a
 * gravação falhar, a lista é recarregada.
 */
import React, { useState } from 'react';

import { useAppDispatch } from 'store/hooks';
import {
  setConsumerUnitOrder,
  setConsumerUnitRefresh,
} from 'store/consumer_unit/consumer_unit.slice';
import { reorderConsumerUnitsService } from 'services/consumer_unit.service';
import { alert } from 'lib/alert';
import { moveByStep, moveToPosition } from 'utils/consumerUnitOrder';

export const useConsumerUnitReorder = (orderedIds: string[]) => {
  const dispatch = useAppDispatch();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const persist = async (ids: string[]) => {
    if (ids.join() === orderedIds.join()) return;
    dispatch(setConsumerUnitOrder({ ids }));
    try {
      await reorderConsumerUnitsService(ids);
    } catch {
      alert.error('Não foi possível salvar a nova ordem dos imóveis.');
      dispatch(setConsumerUnitRefresh({ refresh: true }));
    }
  };

  const bind = (id: string) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      setDraggingId(id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
    },
    onDragOver: (e: React.DragEvent) => {
      if (!draggingId || draggingId === id) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (overId !== id) setOverId(id);
    },
    onDragLeave: () => {
      if (overId === id) setOverId(null);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      const moved = draggingId ?? e.dataTransfer.getData('text/plain');
      setDraggingId(null);
      setOverId(null);
      if (moved) persist(moveToPosition(orderedIds, moved, id));
    },
    onDragEnd: () => {
      setDraggingId(null);
      setOverId(null);
    },
  });

  const handleKey = (id: string) => (e: React.KeyboardEvent) => {
    const step =
      e.key === 'ArrowLeft' || e.key === 'ArrowUp'
        ? -1
        : e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : 0;
    if (!step) return;
    e.preventDefault();
    persist(moveByStep(orderedIds, id, step));
  };

  return { bind, handleKey, draggingId, overId };
};
